import { FailureReason, ProviderExtras, ProviderId } from '@/features/Auth/providers/types'

/**
 * What signing in can carry over from the credential into the form, when this server turns
 * out not to know the person yet.
 */
export type Prefill = ProviderExtras & {
  uid: string
}

/**
 * Where someone is in signing in.
 *
 * Before this, the answer existed only as a consequence of what had already happened: a route
 * param said whether the signup form was reached socially, the selected form config said
 * which fields it would validate, and four pub/sub topics stood in for the transitions
 * between them. None of the three was the state; each was a partial trace of it, and nothing
 * could be asked "what happens next" without performing it. See EPIC-005.
 */
export type AuthState =
  /** Nobody is signing in. */
  | { status: 'anonymous' }
  /** A provider is being asked for a credential. */
  | { status: 'authenticating'; provider: ProviderId }
  /** A credential is being redeemed for a Firebase session. */
  | { status: 'exchanging'; provider: ProviderId }
  /** Signed in; asking this server whether it knows the identity. */
  | { status: 'resolving'; provider: ProviderId; uid: string; extras?: ProviderExtras }
  /** Signed in, and this server has no profile for the identity: it has to be created. */
  | { status: 'registering'; provider: ProviderId; prefill: Prefill; needsPassword: boolean }
  /**
   * Done. `viaRegistration` says a profile was just created here, which means the signup
   * screen owns what happens next — it has its own confirmation step. Reaching this by
   * resolving an existing profile has no such owner, and the flow lands the person itself.
   */
  | { status: 'ready'; uid: string; viaRegistration: boolean }
  /** Not done, and the reason is the only thing that decides how it is shown. */
  | { status: 'failed'; provider: ProviderId; reason: FailureReason; message?: string }

export type AuthEvent =
  | { type: 'signInRequested'; provider: ProviderId }
  | { type: 'credentialObtained' }
  | { type: 'sessionEstablished'; uid: string; extras?: ProviderExtras }
  /** This server has a profile for the identity. Whether it is *complete* is not asked
   * here: the product demands the remaining details when an action needs them, through
   * `ensureProfileCompletion`, not as a step of signing in. */
  | { type: 'profileResolved' }
  | { type: 'profileAbsent' }
  | { type: 'profileCreated'; uid: string }
  | { type: 'failed'; reason: FailureReason; message?: string }
  | { type: 'signedOut' }

export const anonymous: AuthState = { status: 'anonymous' }
